import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useMagnetic } from "@/hooks/useMagnetic";
import { useTr } from "@/autoTranslate";
import { useSiteContent } from "@/siteContent";

const isVideo = (url) => typeof url === "string" && /\.(mp4|webm|mov)(\?|$)/i.test(url);

export default function Hero() {
  const tr = useTr();
  const site = useSiteContent();
  const heroMedia = site.get("photos.hero");
  const fullName = site.get("hero.name") || "Duru Ünalan";
  const nameParts = fullName.split(" ");
  const firstName = nameParts[0];
  const lastName = nameParts.slice(1).join(" ");
  const ref = useRef(null);
  const workBtnRef = useMagnetic(0.35, 140);
  const contactBtnRef = useMagnetic(0.35, 140);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const rot = useTransform(scrollYProgress, [0, 1], [0, -8]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const onMove = (e) => {
      setMouse({
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const line = {
    hidden: { y: "110%" },
    show: (i) => ({
      y: "0%",
      transition: {
        delay: 0.15 + i * 0.11,
        duration: 1.05,
        ease: [0.16, 1, 0.3, 1],
      },
    }),
  };

  return (
    <section
      id="intro"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-28 pb-16 px-6 md:px-10 overflow-hidden"
    >
      {/* Floating parallax portrait card */}
      <motion.div
        style={{ y, rotate: rot, scale }}
        className="hidden lg:block absolute right-[14%] top-[18%] w-[300px] h-[400px] z-10"
      >
        <motion.div
          animate={{
            x: (mouse.x - 0.5) * 30,
            y: (mouse.y - 0.5) * 24,
          }}
          transition={{ type: "spring", stiffness: 40, damping: 15 }}
          className="photo-frame w-full h-full rounded-sm overflow-hidden"
          data-testid="hero-photo-frame"
        >
          {heroMedia ? (
            isVideo(heroMedia) ? (
              <video
                src={heroMedia}
                autoPlay
                muted
                loop
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <img
                src={heroMedia}
                alt="Duru Ünalan"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )
          ) : (
            <div className="absolute inset-0 flex items-center justify-center flex-col gap-2">
              <div className="display-sans text-7xl text-[color:var(--ink)] opacity-30">
                DÜ
              </div>
              <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-60">
                PHOTO — 001 / place portrait here
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* Top meta strip */}
      <div className="hair-b pb-4 flex items-center justify-between font-mono-label text-[11px] uppercase tracking-widest opacity-80">
        <span>{tr("Portfolyo · Vol. 03 · '26 Baskısı")}</span>
        <span className="hidden md:inline">
          {tr("Bilkent Üniversitesi · Uluslararası İlişkiler")}
        </span>
      </div>

      {/* Main type stack */}
      <div className="relative mt-8 md:mt-16 z-20">
        <motion.div
          initial="hidden"
          animate="show"
          className="flex items-baseline gap-6 flex-wrap"
        >
          <span className="line-mask">
            <motion.span
              custom={0}
              variants={line}
              className="display-sans text-[18vw] md:text-[13vw] leading-[0.85]"
            >
              {firstName}
            </motion.span>
          </span>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="show"
          className="mt-2 md:mt-0 flex items-baseline gap-4 md:gap-6 flex-wrap"
        >
          <span className="line-mask">
            <motion.span
              custom={2}
              variants={line}
              className="display-serif italic text-[20vw] md:text-[15vw]"
            >
              {lastName}
            </motion.span>
          </span>
          <span className="line-mask">
            <motion.span
              custom={1}
              variants={line}
              className="chapter-num text-[6vw] md:text-[3vw] font-serif-editorial italic"
            >
              {tr("’ın portfolyosu")}
            </motion.span>
          </span>
          <span className="line-mask ml-0 md:ml-2 basis-full md:basis-auto">
            <motion.span
              custom={3}
              variants={line}
              className="display-sans text-[10vw] md:text-[6vw] text-[color:var(--cobalt)]"
            >
              {tr(site.get("hero.tag"))}
            </motion.span>
          </span>
        </motion.div>

        {/* Bottom row */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-6 items-center"
        >
          <p className="col-span-1 md:col-span-6 text-lg md:text-xl leading-snug max-w-md">
            {tr(site.get("hero.blurb"))}
          </p>
          <div className="col-span-1 md:col-span-6 flex gap-3 md:justify-center">
            <button
              data-testid="hero-cta-work"
              ref={workBtnRef}
              data-cursor="hover"
              data-cursor-label="view"
              className="btn-pill"
              onClick={() =>
                window.__lenis
                  ? window.__lenis.scrollTo("#works")
                  : document.getElementById("works").scrollIntoView({ behavior: "smooth" })
              }
            >
              {tr("İşleri gör")}
              <span aria-hidden>↓</span>
            </button>
            <button
              data-testid="hero-cta-contact"
              ref={contactBtnRef}
              data-cursor="hover"
              data-cursor-label="say hi"
              className="btn-pill ghost"
              onClick={() =>
                window.__lenis
                  ? window.__lenis.scrollTo("#contact")
                  : document.getElementById("contact").scrollIntoView({ behavior: "smooth" })
              }
            >
              {tr("Merhaba de")}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
